import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    EventEmitter,
    OnInit,
    Output,
    computed,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { IimSpinnerModule } from '@iim-angular/components';
import { NotificationMessageLevel, NotificationService } from '@lib/core';
import { BaseSiteLayoutModule } from '@lib/util/base-site-layout';
import { TranslateModule } from '@ngx-translate/core';
import { firstValueFrom, forkJoin } from 'rxjs';
import { PurchasingGroup } from '../models/purchasing-group.model';
import { UpdatePurchasingGroupSettingsCommand } from '../models/update-purchasing-group-settings-command.model';
import { PurchasingGroupDataService } from '../services/purchasing-group-data.service';

interface IPurchasingGroupSettingsForm {
    readonly required: FormControl<boolean>;
    readonly showDuringOrderingProcess: FormControl<boolean>;
    readonly defaultPurchasingGroupId: FormControl<string | null>;
}

export class PurchasingGroupSettingsDialogResult {
    constructor(public readonly updated: boolean) {}
}

@Component({
    selector: 'lib-purchasing-group-settings',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        BaseSiteLayoutModule,
        IimSpinnerModule,
        MatSlideToggleModule,
        TranslateModule,
    ],
    templateUrl: './purchasing-group-settings.component.html',
    styleUrls: ['./purchasing-group-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PurchasingGroupSettingsComponent implements OnInit {
    private readonly fb = inject(FormBuilder);
    private readonly dataService = inject(PurchasingGroupDataService);
    private readonly notificationService = inject(NotificationService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly dialogRef = inject<MatDialogRef<PurchasingGroupSettingsComponent, PurchasingGroupSettingsDialogResult> | null>(
        MatDialogRef,
        { optional: true },
    );

    @Output() public readonly close = new EventEmitter<void>();
    @Output() public readonly saved = new EventEmitter<void>();
    public readonly loading = signal(true);
    public readonly saving = signal(false);
    public readonly loadError = signal(false);
    public readonly formDirty = signal(false);
    public readonly purchasingGroups = signal<ReadonlyArray<PurchasingGroup>>([]);
    public readonly saveDisabled = computed(
        () => this.loading() || this.saving() || this.loadError() || !this.formDirty(),
    );

    public readonly form: FormGroup<IPurchasingGroupSettingsForm> = this.fb.group<IPurchasingGroupSettingsForm>({
        required: this.fb.control(false, { nonNullable: true }),
        showDuringOrderingProcess: this.fb.control(true, { nonNullable: true }),
        defaultPurchasingGroupId: this.fb.control<string | null>(null),
    });

    constructor() {
        this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.formDirty.set(this.form.dirty);
        });
    }

    public async ngOnInit(): Promise<void> {
        await this.loadSettings();
    }

    public async save(): Promise<void> {
        if (this.saveDisabled()) {
            return;
        }

        this.saving.set(true);

        try {
            const { required, showDuringOrderingProcess, defaultPurchasingGroupId } = this.form.getRawValue();
            const settingsCommand = new UpdatePurchasingGroupSettingsCommand(
                required,
                showDuringOrderingProcess,
                defaultPurchasingGroupId,
            );

            await firstValueFrom(this.dataService.updateSettings(settingsCommand));

            this.form.markAsPristine();
            this.formDirty.set(false);
            this.notificationService.showNotification(
                marker('Einstellungen wurden gespeichert.'),
                NotificationMessageLevel.SUCCESS,
            );
            this.saved.emit();
            this.close.emit();
            this.dialogRef?.close(new PurchasingGroupSettingsDialogResult(true));
        } catch (error) {
            const message = this.dataService.resolveErrorMessage(
                error,
                marker('Einstellungen konnten nicht gespeichert werden.'),
            );
            this.notificationService.showNotification(message, NotificationMessageLevel.ERROR);
        } finally {
            this.saving.set(false);
        }
    }

    public onShowDuringOrderingProcessChange(): void {
        this.applyShowDependency(true);
    }

    // A hidden purchasing-group field is never enforced, so "Required field" only makes sense while
    // the field is shown during the ordering process. When the field is hidden we reset required to
    // false and disable the toggle; when it is shown again the toggle becomes editable.
    private applyShowDependency(emitEvent: boolean): void {
        const showDuringOrderingProcess = this.form.controls.showDuringOrderingProcess.value;
        const requiredControl = this.form.controls.required;

        if (showDuringOrderingProcess) {
            requiredControl.enable({ emitEvent });
        } else {
            requiredControl.setValue(false, { emitEvent });
            requiredControl.disable({ emitEvent });
        }
    }

    public cancel(): void {
        this.close.emit();
        this.dialogRef?.close(new PurchasingGroupSettingsDialogResult(false));
    }

    private async loadSettings(): Promise<void> {
        this.loading.set(true);
        this.loadError.set(false);

        try {
            const [settings, groups] = await firstValueFrom(
                forkJoin([this.dataService.getSettings(), this.dataService.getSelectable()]),
            );

            this.purchasingGroups.set(groups);
            this.form.setValue({
                required: settings.required,
                showDuringOrderingProcess: settings.showDuringOrderingProcess,
                defaultPurchasingGroupId: settings.defaultPurchasingGroupId,
            });
            this.applyShowDependency(false);
            this.form.markAsPristine();
            this.formDirty.set(false);
        } catch (error) {
            this.loadError.set(true);
            const message = this.dataService.resolveErrorMessage(
                error,
                marker('Einstellungen konnten nicht geladen werden.'),
            );
            this.notificationService.showNotification(message, NotificationMessageLevel.ERROR);
        } finally {
            this.loading.set(false);
        }
    }
}
